import { Components, Theme } from '@mui/material'
import { insetBorder } from './insetBorder'

declare module '@mui/material/TextField' {
  interface BaseTextFieldProps {
    'data-is-ai-prefilled'?: boolean
    // Theme 2 inputs only support the standard variant.
    variant: 'standard'
  }
}

const aiPrefillStyle = (theme: Theme) => ({
  backgroundColor: theme.palette.bg.ai.muted,
  boxShadow: insetBorder({
    width: theme.strokeWidth.default,
    color: theme.palette.border.ai.subtle,
  }),
  '& .MuiInput-input': {
    color: theme.palette.text.ai.default,
  },
  '&:hover': {
    boxShadow: insetBorder({
      width: theme.strokeWidth.default,
      color: theme.palette.border.ai.strongest,
    }),
  },
  '&.Mui-focused': {
    boxShadow: `${insetBorder({
      width: theme.strokeWidth.default,
      color: theme.palette.border.ai.strongest,
    })}, ${theme.customShadows.activeAi}`,
  },
})

export const MuiTextField: Components<Omit<Theme, 'components'>>['MuiTextField'] = {
  defaultProps: {
    variant: 'standard',
    size: 'medium',
  },
  variants: [
    {
      // AI Prefill state
      // This only ends up on the TextField, not the input
      props: { 'data-is-ai-prefilled': true },
      style: ({ theme }) => ({
        '& .MuiInput-root:not(.Mui-disabled)': aiPrefillStyle(theme),
      }),
    },
  ],
}

export const MuiInputBase: Components<Omit<Theme, 'components'>>['MuiInputBase'] = {
  styleOverrides: {
    input: ({ theme }) => ({
      '&.Mui-disabled': {
        color: theme.palette.text.default,
        WebkitTextFillColor: theme.palette.text.default,
      },
    }),
  },
}

export const MuiInput: Components<Omit<Theme, 'components'>>['MuiInput'] = {
  styleOverrides: {
    underline: {
      '&::before, &::after': {
        display: 'none',
      },
    },
    root: ({ theme }) => ({
      // Override MUI default spacing, we've got ours on the label
      'label + &': {
        marginTop: 0,
      },
      backgroundColor: theme.palette.bg.surface.default,
      borderRadius: theme.shape['radius-lg'],
      boxShadow: insetBorder({
        width: theme.strokeWidth.default,
        color: theme.palette.border.strong,
      }),
      '&:not(.Mui-disabled, .Mui-focused):hover': {
        boxShadow: insetBorder({
          width: theme.strokeWidth.default,
          color: theme.palette.border.strongest,
        }),
      },
      '&.Mui-focused': {
        boxShadow: `${insetBorder({
          width: theme.strokeWidth.default,
          color: theme.palette.border.strongest,
        })}, ${theme.customShadows.activeFocus}`,
      },

      '&.Mui-error': {
        backgroundColor: theme.palette.bg.danger.muted,
        boxShadow: insetBorder({
          width: theme.strokeWidth.default,
          color: theme.palette.border.danger.strong,
        }),
        '&:hover': {
          boxShadow: insetBorder({
            width: theme.strokeWidth.default,
            color: theme.palette.border.danger.strongest,
          }),
        },
        '&.Mui-focused': {
          boxShadow: `${insetBorder({
            width: theme.strokeWidth.default,
            color: theme.palette.border.danger.strongest,
          })}, ${theme.customShadows.activeError}`,
        },
        '& .MuiInput-input, & .MuiInputAdornment-root, & .MuiInputAdornment-root .MuiTypography-root': {
          color: theme.palette.text.danger.onDangerMuted,
        },
      },

      '&.MuiInputBase-colorSuccess': {
        backgroundColor: theme.palette.bg.success.muted,
        boxShadow: insetBorder({
          width: theme.strokeWidth.default,
          color: theme.palette.border.success.subtle,
        }),
        '&:hover': {
          boxShadow: insetBorder({
            width: theme.strokeWidth.default,
            color: theme.palette.border.success.strongest,
          }),
        },
        '&.Mui-focused': {
          boxShadow: `${insetBorder({
            width: theme.strokeWidth.default,
            color: theme.palette.border.success.strongest,
          })}, ${theme.customShadows.activeSuccess}`,
        },
        '& .MuiInput-input': {
          color: theme.palette.text.success.onSuccessMuted,
        },
        // colorSuccess doesn't get applied to the MuiFormHelperText, so we have to target it with a sibling selector
        '& ~ .MuiFormHelperText-root': {
          color: theme.palette.text.success.default,
        },
      },

      '&.Mui-disabled': {
        backgroundColor: theme.palette.bg.surface.subtle,
        '& .MuiInput-input': {
          // Disabled text
          WebkitTextFillColor: theme.palette.text.default,
        },
      },

      '&[data-is-ai-prefilled="true"]': aiPrefillStyle(theme),

      // Sizes
      variants: [
        {
          props: { size: 'small' },
          style: {
            padding: theme.spacing(1.5, 2.5, 1.5, 3),
          },
        },
        {
          props: { size: 'medium' },
          style: {
            padding: theme.spacing(2, 2.5, 2, 3),
          },
        },
      ],
    }),

    input: ({ theme }) => ({
      ...theme.typography['body-regular'],
      color: theme.palette.text.default,
      padding: 0,

      // Designs use Typography/LineHeight/base = 24px
      // But line-height doesn't really work for inputs, so minHeight
      minHeight: '24px',

      '&::placeholder': {
        ...theme.typography['body-regular'],
        opacity: 1,
        color: theme.palette.text.subtle,
      },
    }),
  },
}

export const MuiInputLabel: Components<Omit<Theme, 'components'>>['MuiInputLabel'] = {
  defaultProps: {
    shrink: true, // We always want the label shrunk (floating) because we don't use the native placeholder.
    margin: 'dense', // Tighter spacing by default, since the label is always floating.
  },
  styleOverrides: {
    root: ({ theme }) => ({
      // Combined with shrink: true, this makes the label sit statically above the input, rather than floating
      position: 'relative',
      transform: 'unset',

      // Styling
      ...theme.typography['body-regular'],
      color: theme.palette.text.default,
      // Don't change label colour on focus, error, disabled
      '&.Mui-focused, &.Mui-error, &.Mui-disabled': {
        color: theme.palette.text.default,
      },

      // Flex so that we can put InfoTooltip after the asterisk
      display: 'flex',
      alignItems: 'center',
      '& .information-tooltip': {
        order: 100,
        marginLeft: theme.spacing(0.5),
        '&.information-tooltip-pinned-end': {
          marginLeft: 'auto',
        },
      },

      marginBottom: theme.spacing(1.5),
    }),
    asterisk: ({ theme }) => ({
      color: theme.palette.text.danger.default,
      marginLeft: theme.spacing(0.5),
    }),
  },
}

export const MuiFormLabel: Components<Omit<Theme, 'components'>>['MuiFormLabel'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      '&.Mui-error': {
        color: theme.palette.text.danger.default,
      },
    }),
    asterisk: ({ theme }) => ({
      color: theme.palette.text.danger.default,
    }),
  },
}

export const MuiFormHelperText: Components<Omit<Theme, 'components'>>['MuiFormHelperText'] = {
  defaultProps: {
    margin: 'dense',
  },
  styleOverrides: {
    root: ({ theme }) => ({
      marginLeft: 0,
      marginTop: theme.spacing(1.5),
      ...theme.typography['small-regular'],
      color: theme.palette.text.subtle,
      // Don't fade the helper text when disabled
      '&.Mui-disabled': {
        color: theme.palette.text.subtle,
      },
      '&.Mui-error': {
        color: theme.palette.text.danger.default,
      },
    }),
  },
}

export const MuiInputAdornment: Components<Omit<Theme, 'components'>>['MuiInputAdornment'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      color: theme.palette.fg.subtle,
      // Without these, occasionally the adornment can overflow
      minWidth: 0,
      flexShrink: 0,
      '&.MuiInputAdornment-positionEnd .MuiIconButton-root': {
        // Offset the icon button spacing - equivalent to (and overrides) edge="end"
        marginRight: theme.spacing(-0.5),
      },
      // MUI wraps string adornments in a Typography component
      '& .MuiTypography-root': {
        ...theme.typography['body-regular'],
        color: theme.palette.text.subtle,
      },
    }),
  },
}
